import { z } from 'zod';
import { getDurableItem, removeDurableItem, removePersistentItem, setDurableItem } from '../../vrs-auth/storage';
import { currentCallHistoryScope, isCurrentCallHistoryScope } from './callHistoryStorage';
import { voicemailMetadataSchema } from './components/vrs/voicemailInboxData';
import { mobileLog } from './logging';
export const VOICEMAIL_CACHE_KEY = 'vrs_voicemails_scoped';
const ownerSchema = z.object({ origin: z.string().min(1), tenantId: z.string(), userId: z.string().min(1),
    role: z.literal('client') });
const cacheSchema = z.object({ version: z.literal(1), owner: ownerSchema,
    messages: z.array(voicemailMetadataSchema).max(50), total: z.number().int().nonnegative(), unreadCount: z.number().int().nonnegative() });
const empty = { messages: [], total: 0, unreadCount: 0, incomplete: false };
let snapshot = null;
let revision = 0;
let writes = Promise.resolve(true);
export function currentVoicemailScope() {
    const scope = currentCallHistoryScope();
    return scope?.owner.role === 'client' ? scope : null;
}
export function readVoicemailCache(scope = currentVoicemailScope()) {
    return scope && isCurrentCallHistoryScope(scope) && snapshot?.owner === JSON.stringify(scope.owner) ? snapshot.inbox : empty;
}
export async function hydrateVoicemailCache(scope = currentVoicemailScope()) {
    if (!scope)
        return;
    const started = revision;
    try {
        await writes;
        const raw = await getDurableItem(VOICEMAIL_CACHE_KEY);
        if (revision !== started || !isCurrentCallHistoryScope(scope))
            return;
        if (raw === null) {
            snapshot = { owner: JSON.stringify(scope.owner), inbox: empty };
            return;
        }
        const parsed = cacheSchema.parse(JSON.parse(raw));
        if (JSON.stringify(parsed.owner) !== JSON.stringify(scope.owner))
            return;
        if (new Set(parsed.messages.map(message => message.id)).size !== parsed.messages.length
            || parsed.total < parsed.messages.length || parsed.unreadCount > parsed.total)
            throw new Error('Invalid saved inbox');
        snapshot = { owner: JSON.stringify(scope.owner), inbox: { messages: parsed.messages,
                total: parsed.total, unreadCount: parsed.unreadCount, incomplete: false } };
    }
    catch {
        if (revision === started && isCurrentCallHistoryScope(scope)) {
            snapshot = { owner: JSON.stringify(scope.owner), inbox: { ...readVoicemailCache(scope), incomplete: true } };
            mobileLog('warn', 'voicemail_cache_read_failed');
        }
    }
}
function persist(operation, scope) {
    writes = writes.then(async () => {
        if (scope && !isCurrentCallHistoryScope(scope))
            return false;
        try {
            await operation();
            return !scope || isCurrentCallHistoryScope(scope);
        }
        catch {
            mobileLog('warn', 'voicemail_cache_write_failed');
            return false;
        }
    });
    return writes;
}
export function saveVoicemailCache(scope, inbox) {
    if (!isCurrentCallHistoryScope(scope) || scope.owner.role !== 'client')
        return Promise.resolve(false);
    const checked = cacheSchema.safeParse({ version: 1, owner: scope.owner, messages: inbox.messages.slice(0, 50),
        total: inbox.total, unreadCount: inbox.unreadCount });
    if (!checked.success || checked.data.total < checked.data.messages.length || checked.data.unreadCount > checked.data.total
        || new Set(checked.data.messages.map(message => message.id)).size !== checked.data.messages.length) {
        mobileLog('warn', 'voicemail_cache_invalid_data');
        return Promise.resolve(false);
    }
    revision++;
    const { messages, total, unreadCount } = checked.data;
    snapshot = { owner: JSON.stringify(scope.owner), inbox: { messages, total, unreadCount, incomplete: false } };
    return persist(() => setDurableItem(VOICEMAIL_CACHE_KEY, JSON.stringify(checked.data)), scope);
}
/** Remove stale disk data before an irreversible server deletion; keep the screen's current rows separately. */
export function invalidateVoicemailCache(scope) {
    if (!isCurrentCallHistoryScope(scope))
        return Promise.resolve(false);
    revision++;
    snapshot = null;
    return persist(() => removeDurableItem(VOICEMAIL_CACHE_KEY), scope);
}
export function clearVoicemailCache() {
    revision++;
    snapshot = null;
    removePersistentItem('vrs_voicemails');
    return persist(() => removeDurableItem(VOICEMAIL_CACHE_KEY));
}
